import Link from 'next/link'

import { AnchorLink } from '@/ui/components/ui/anchor-link'
import { AnchorHTMLAttributes, ReactNode } from 'react'

type Variant = 'solid' | 'solid-light' | 'outline' | 'ghost' | 'urgent'

const base =
  'group relative inline-flex items-center justify-center gap-3 ' +
  'px-7 py-4 rounded-[3px] font-mono text-[0.6875rem] uppercase tracking-[0.16em] font-medium ' +
  'transition-[background-color,border-color,color,transform] duration-300 ease-out ' +
  'active:translate-y-px'

const variants: Record<Variant, string> = {
  // Action principale : bloc d'encre, sans fioriture.
  solid: 'bg-ink text-chalk hover:bg-anthracite',
  // Même rôle, posé sur fond sombre.
  'solid-light': 'bg-chalk text-ink hover:bg-white',
  // Action secondaire : filet seul.
  outline:
    'border border-ink/25 text-ink hover:border-ink hover:bg-ink hover:text-chalk',
  // Sur fond sombre.
  ghost:
    'border border-chalk/25 text-chalk hover:border-chalk hover:bg-chalk hover:text-ink',
  // Réservé à l'urgence. Le signal ne sert à rien d'autre.
  // Texte encre et non blanc : blanc sur #F0531C ne fait que 3,5:1,
  // sous le seuil AA. L'encre monte à 5,1:1 — et c'est la combinaison
  // de la signalétique de chantier.
  urgent: 'bg-signal text-ink hover:bg-ink hover:text-signal',
}

type ActionProps = {
  href: string
  children: ReactNode
  variant?: Variant
  className?: string
  trailing?: ReactNode
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'children'>

export function Action({
  href,
  children,
  variant = 'solid',
  className = '',
  trailing,
  ...rest
}: ActionProps) {
  const classes = `${base} ${variants[variant]} ${className}`
  const content = (
    <>
      <span>{children}</span>
      {trailing ?? (
        <span
          aria-hidden="true"
          className="transition-transform duration-300 group-hover:translate-x-1"
        >
          &rarr;
        </span>
      )}
    </>
  )

  // tel:, mailto: et liens absolus restent des <a> natifs
  if (/^(tel:|mailto:|https?:)/.test(href)) {
    return (
      <a href={href} className={classes} {...rest}>
        {content}
      </a>
    )
  }

  // Les ancres passent par AnchorLink : sur la page cible, il rend une
  // ancre native, seule capable de redéfiler vers un fragment déjà
  // présent dans l'URL.
  if (href.includes('#')) {
    return (
      <AnchorLink href={href} className={classes} {...rest}>
        {content}
      </AnchorLink>
    )
  }

  return (
    <Link href={href} className={classes} {...rest}>
      {content}
    </Link>
  )
}

/**
 * Le numéro de téléphone en tant qu'objet typographique.
 * C'est l'action la plus rentable du site : elle est traitée comme un
 * chiffre coté, pas comme un bouton.
 */
export function PhoneAction({
  label,
  tone = 'dark',
  size = 'md',
  className = '',
}: {
  /** Sur-titre monospace. Doit venir de la copie existante. */
  label?: string
  tone?: 'dark' | 'light'
  size?: 'md' | 'lg'
  className?: string
}) {
  const isLight = tone === 'light'

  return (
    // Pas d'aria-label : il remplaçait le texte visible par un libellé
    // différent, ce que WCAG 2.5.3 (Label in Name) interdit. Le contenu
    // visible fait déjà un bon nom accessible.
    <a
      href="tel:+33620111014"
      className={`group inline-flex flex-col gap-2 ${className}`}
    >
      {label ? (
        <span className={`annot ${isLight ? 'text-mist' : 'text-graphite'}`}>
          {label}
        </span>
      ) : null}
      <span
        data-figure
        className={`font-display font-bold tracking-tight leading-none whitespace-nowrap transition-colors ${
          size === 'lg'
            ? 'text-3xl tablet:text-5xl desktop:text-6xl'
            : 'text-[1.75rem] tablet:text-3xl desktop:text-4xl'
        } ${isLight ? 'text-chalk' : 'text-ink'} group-hover:text-signal`}
      >
        06&nbsp;20&nbsp;11&nbsp;10&nbsp;14
      </span>
    </a>
  )
}
